<template>
  <section class="agent-prompt-templates" :aria-labelledby="titleId">
    <div class="agent-prompt-template-head">
      <div>
        <h5 :id="titleId">Template Prompt AI</h5>
        <p>Mulai dari persona siap pakai, lalu sesuaikan isinya dengan bisnis Anda.</p>
      </div>
      <span
        v-if="selectedTemplateId"
        class="agent-prompt-template-current"
        :class="{ 'is-edited': dirty }"
      >
        <i :class="selectedTemplateIcon"></i>
        {{ selectedTemplateDisplay }}
        <small v-if="dirty && selectedTemplateId !== 'custom'">Diedit manual</small>
      </span>
      <span v-else class="agent-prompt-template-current is-empty">
        <i class="fa fa-pencil"></i>
        Belum memakai template
      </span>
    </div>

    <div class="agent-prompt-template-grid">
      <article
        v-for="template in templates"
        :key="template.id"
        class="agent-prompt-template-card"
        :class="{ active: selectedTemplateId === template.id }"
      >
        <span class="agent-prompt-template-icon">
          <i :class="template.icon"></i>
        </span>
        <strong>{{ template.name }}</strong>
        <p>{{ template.description }}</p>
        <button
          type="button"
          class="agent-prompt-template-use"
          @click="useTemplate(template)"
        >
          Gunakan Template
        </button>
      </article>
    </div>

    <div class="agent-prompt-template-custom">
      <button
        type="button"
        class="agent-prompt-template-custom-btn"
        :class="{ active: selectedTemplateId === 'custom' }"
        @click="useCustom"
      >
        <i class="fa fa-pencil"></i>
        Tulis Sendiri
      </button>
      <small>Mulai dari prompt kosong dan tulis sesuai kebutuhan.</small>
    </div>
  </section>
</template>

<script>
import {
  PROMPT_TEMPLATES,
  findPromptTemplate,
  normalizePrompt
} from "../promptTemplates";

export default {
  name: "PromptTemplatePicker",
  props: {
    value: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      templates: PROMPT_TEMPLATES,
      selectedTemplateId: "",
      dirty: false,
      applyingTemplate: false
    };
  },
  computed: {
    titleId() {
      return "agent-prompt-template-title-" + this._uid;
    },
    selectedTemplate() {
      return this.templates.find(item => item.id === this.selectedTemplateId) || null;
    },
    selectedTemplateDisplay() {
      if (this.selectedTemplate) return "Template: " + this.selectedTemplate.name;
      if (this.selectedTemplateId === "custom") return "Tulis Sendiri";
      return "";
    },
    selectedTemplateIcon() {
      if (this.selectedTemplate) return this.selectedTemplate.icon;
      return "fa fa-pencil";
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (this.applyingTemplate) return;
        this.syncFromValue(value);
      }
    }
  },
  methods: {
    syncFromValue(value) {
      const template = this.selectedTemplate;
      if (this.selectedTemplateId === "custom") {
        this.dirty = !!normalizePrompt(value);
        return;
      }
      if (template) {
        this.dirty = normalizePrompt(value) !== normalizePrompt(template.prompt);
        return;
      }

      const matched = findPromptTemplate(value);
      if (matched) {
        this.selectedTemplateId = matched.id;
        this.dirty = false;
        return;
      }
      if (normalizePrompt(value)) {
        this.selectedTemplateId = "custom";
        this.dirty = true;
        return;
      }
      this.selectedTemplateId = "";
      this.dirty = false;
    },
    async confirmReplace(message) {
      if (!this.$fire) return true;
      const result = await this.$fire({
        title: "Ganti isi Prompt AI?",
        text: message,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, ganti",
        cancelButtonText: "Batal"
      });
      return !!(result && result.value);
    },
    async useTemplate(template) {
      if (!template) return;
      const current = normalizePrompt(this.value);
      const target = normalizePrompt(template.prompt);
      if (current && current !== target && this.dirty) {
        const confirmed = await this.confirmReplace(
          `Prompt saat ini sudah diubah. Menggunakan template ${template.name} akan mengganti seluruh isi prompt.`
        );
        if (!confirmed) return;
      }

      this.applyingTemplate = true;
      this.selectedTemplateId = template.id;
      this.dirty = false;
      this.$emit("input", template.prompt);
      this.$nextTick(() => {
        this.applyingTemplate = false;
      });
    },
    async useCustom() {
      const current = normalizePrompt(this.value);
      if (current) {
        const confirmed = await this.confirmReplace(
          "Prompt saat ini akan dikosongkan agar Anda bisa menulis dari awal."
        );
        if (!confirmed) return;
      }

      this.applyingTemplate = true;
      this.selectedTemplateId = "custom";
      this.dirty = false;
      this.$emit("input", "");
      this.$nextTick(() => {
        this.applyingTemplate = false;
      });
    }
  }
};
</script>

<style scoped>
.agent-prompt-templates {
  margin-bottom: 16px;
  border: 1px solid #edf1f6;
  border-radius: 12px;
  background: #fbfcfe;
  padding: 13px;
}
.agent-prompt-template-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 11px;
}
.agent-prompt-template-head h5 {
  margin: 0 0 3px;
  color: #344054;
  font-size: 13px;
  font-weight: 800;
}
.agent-prompt-template-head p {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.45;
}
.agent-prompt-template-current {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 5px;
  border-radius: 999px;
  background: #fff3e3;
  color: #b45309;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.agent-prompt-template-current.is-edited {
  background: #fff8eb;
  color: #9a5a00;
}
.agent-prompt-template-current.is-empty {
  background: #f4f7fb;
  color: #98a2b3;
  font-weight: 600;
}
.agent-prompt-template-current small {
  border-radius: 999px;
  background: rgba(180, 83, 9, .1);
  padding: 2px 5px;
  font-size: 12px;
  font-weight: 800;
}
.agent-prompt-template-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
}
.agent-prompt-template-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid #e7eaf0;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
  transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease;
}
.agent-prompt-template-card.active {
  border-color: #fe9b44;
  background: #fff8f1;
  box-shadow: 0 0 0 1px rgba(254, 155, 68, .15);
}
.agent-prompt-template-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-bottom: 7px;
  border-radius: 8px;
  background: #fff3e3;
  color: #e77f25;
  font-size: 13px;
}
.agent-prompt-template-card strong {
  display: block;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.agent-prompt-template-card p {
  flex: 1 1 auto;
  margin: 4px 0 9px;
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.45;
}
.agent-prompt-template-use {
  align-self: flex-start;
  border: 1px solid #f7c98b;
  border-radius: 7px;
  background: #fffaf3;
  color: #b45309;
  padding: 5px 8px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}
.agent-prompt-template-use:hover {
  border-color: #fe9b44;
  background: #fff3e3;
}
.agent-prompt-template-custom {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 9px;
}
.agent-prompt-template-custom-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  background: #fff;
  color: #475467;
  padding: 6px 9px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.agent-prompt-template-custom-btn:hover,
.agent-prompt-template-custom-btn.active {
  border-color: #fe9b44;
  color: #b45309;
  background: #fff8f1;
}
.agent-prompt-template-custom small {
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.4;
}
@media (max-width: 991px) {
  .agent-prompt-template-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 575px) {
  .agent-prompt-template-head {
    flex-direction: column;
  }
  .agent-prompt-template-current {
    white-space: normal;
  }
  .agent-prompt-template-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ai/agentV2/components/PromptTemplatePicker.vue